import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCustomSets, createCustomSet, deleteCustomSet, CustomSet } from '../services/api';
import './CustomSets.css';

const ICONS = ['📝', '📚', '🎯', '⭐', '🔥', '💪', '🧠', '📖', '✏️', '🎓', '💡', '🏆'];
const COLORS = ['#607D8B', '#2196F3', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0', '#00BCD4', '#FF5722'];

const CustomSets: React.FC = () => {
  const [sets, setSets] = useState<CustomSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState('📝');
  const [newColor, setNewColor] = useState('#607D8B');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadSets();
  }, []);

  const loadSets = async () => {
    try {
      const data = await getCustomSets();
      setSets(data);
    } catch (err) {
      console.error('Failed to load custom sets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      setError('Podaj nazwę zestawu');
      return;
    }
    try {
      await createCustomSet({ name: newName.trim(), icon: newIcon, color: newColor });
      setNewName('');
      setShowCreate(false);
      setError('');
      loadSets();
    } catch (err) {
      setError('Nie udało się utworzyć zestawu');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć zestaw "${name}"?`)) return;
    try {
      await deleteCustomSet(id);
      loadSets();
    } catch (err) {
      console.error('Failed to delete set:', err);
    }
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="custom-sets-page fade-in">
      <h1>📝 Moje zestawy</h1>
      <p className="subtitle">Twórz własne zestawy słówek do nauki</p>

      <button className="btn btn-primary create-set-btn" onClick={() => setShowCreate(true)}>
        ➕ Utwórz nowy zestaw
      </button>

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Nowy zestaw</h2>
            
            <div className="form-group">
              <label>Nazwa zestawu</label>
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="np. Sprawdzian z Unit 3"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Ikona</label>
              <div className="icon-picker">
                {ICONS.map(icon => (
                  <button
                    key={icon}
                    className={`icon-option ${newIcon === icon ? 'selected' : ''}`}
                    onClick={() => setNewIcon(icon)}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Kolor</label>
              <div className="color-picker">
                {COLORS.map(color => (
                  <button
                    key={color}
                    className={`color-option ${newColor === color ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewColor(color)}
                  />
                ))}
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>
                Anuluj
              </button>
              <button className="btn btn-primary" onClick={handleCreate}>
                Utwórz
              </button>
            </div>
          </div>
        </div>
      )}

      {sets.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>Nie masz jeszcze żadnych zestawów</h3>
          <p>Utwórz swój pierwszy zestaw i dodaj do niego słówka z lekcji lub słownika!</p>
          <p>Zestawy służą do nauki wybranych słówek, np. przed sprawdzianem. Samodzielnie wybierz lekcje i słowa których chcesz się uczyć.</p>
        </div>
      ) : (
        <div className="sets-grid">
          {sets.map(set => (
            <div
              key={set.id}
              className="set-card"
              style={{ '--set-color': set.color } as React.CSSProperties}
            >
              <div className="set-header" onClick={() => navigate(`/custom-sets/${set.id}`)}>
                <span className="set-icon">{set.icon}</span>
                <div className="set-info">
                  <h3>{set.name}</h3>
                  <span className="set-stats">
                    {set.total_words || 0} słów
                    {((set.content_count || 0) > 0 || (set.lesson_count || 0) > 0) && (
                      <span className="set-stats-detail">
                        {' '}({set.lesson_count || 0} lekcji, {set.content_count || 0} słów)
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <div className="set-actions">
                <Link to={`/custom-sets/${set.id}`} className="btn btn-small">
                  ✏️ Edytuj
                </Link>
                <Link to={`/custom-sets/${set.id}/learn`} className="btn btn-small btn-primary">
                  📖 Ucz się
                </Link>
                <button
                  className="btn btn-small btn-danger"
                  onClick={() => handleDelete(set.id, set.name)}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomSets;

